import * as fromActions from '../actions';
import * as fromModels from '../../models';
import { HttpHeaders } from '@angular/common/http';

export interface ContractDetailsState {
  summary: SummaryState;
  requestForm: RequestFormState;
  summarySearch: SummarySearchState;
}

export interface RequestFormState {
  loading: boolean;
  loaded: boolean;
  errors: any;
  id: string;
}

export interface SummarySearchState {
  loading: boolean;
  loaded: boolean;
  errors: any;
  searchCriteria: fromModels.DownloadSummarySearchRequest;
}

export interface SummaryState {
  loading: boolean;
  loaded: boolean;
  errors: any;
  response: fromModels.LoadSummaryResponse;
  searchCriteria: fromModels.LoadSummaryRequest;
  pagingSorting: fromModels.PagingSorting;
}

export const initialState: ContractDetailsState = {
  summary: {
    loading: false,
    loaded: false,
    response: null,
    errors: null,
    searchCriteria: {
      fromDate: null,
      toDate: null,
      orderId: null,
      contractId: null,
      contractNumber: null,
      effectiveFrom: null,
      sort: null,
      take: null,
      skip: null
    },
    pagingSorting: {
      totalCount: null,
      pageSize: null,
      currentPage: null,
      totalPages: null,
      sortBy: null,
      sortDir: null
    }
  },
  summarySearch: {
    errors: null,
    loading: false,
    loaded: false,
    searchCriteria: {
      fromDate: null,
      toDate: null,
      orderId: null,
      contractId: null,
      contractNumber: null,
      effectiveFrom: null,
      skip: null,
      sort: null,
      take: null
    }
  },
  requestForm: {
    loading: false,
    loaded: false,
    errors: null,
    id: null
  }
};

export function reducer(state = initialState, action: fromActions.ContractActions): ContractDetailsState {
  switch (action.type) {
    case fromActions.ContractActionTypes.ProcessQueryString: {
      return {
        ...state,
        summary: {
          ...state.summary,
          loading: false,
          loaded: false,
          errors: null
        }
      };
    }
    case fromActions.ContractActionTypes.LoadSummary: {
      return {
        ...state,
        summary: {
          ...state.summary,
          loading: true,
          loaded: false,
          searchCriteria: action.payload
        }
      };
    }

    case fromActions.ContractActionTypes.LoadSummarySuccess: {
      return {
        ...state,
        summary: {
          ...updateSearchResultsState(state.summary, action.payload, action.httpHeaders),
          loading: false,
          loaded: true
        }
      };
    }

    case fromActions.ContractActionTypes.LoadSummaryFailure: {
      return {
        ...state,
        summary: {
          ...state.summary,
          loading: false,
          loaded: false,
          response: null,
          errors: action.payload
        }
      };
    }

    case fromActions.ContractActionTypes.DownloadSummarySearch: {
      return {
        ...state,
        summarySearch: {
          ...state.summarySearch,
          loading: true,
          loaded: false
        },
        summary: {
          ...state.summary,
          loading: true,
          loaded: false
        }
      };
    }

    case fromActions.ContractActionTypes.DownloadSummarySearchSuccess: {
      return {
        ...state,
        summarySearch: {
          ...state.summarySearch,
          loading: false,
          loaded: true
        },
        summary: {
          ...state.summary,
          loading: false,
          loaded: true
        }
      };
    }

    case fromActions.ContractActionTypes.DownloadSummarySearchFailure: {
      return {
        ...state,
        summarySearch: {
          ...state.summarySearch,
          loading: false,
          loaded: false
        },
        summary: {
          ...state.summary,
          loading: false,
          loaded: false
        }
      };
    }

    case fromActions.ContractActionTypes.DownloadRequestForm: {
      return {
        ...state,
        requestForm: {
          loading: true,
          loaded: false,
          id: null,
          errors: null
        }
      };
    }

    case fromActions.ContractActionTypes.DownloadRequestFormSuccess: {
      return {
        ...state,
        requestForm: {
          ...state.requestForm,
          loading: false,
          loaded: true
        }
      };
    }

    case fromActions.ContractActionTypes.DownloadRequestFormFailure: {
      return {
        ...state,
        requestForm: {
          ...state.requestForm,
          loading: false,
          loaded: false,
          errors: action.payload.error
        }
      };
    }

    case fromActions.ContractActionTypes.ExecuteSearch:
      return {
        ...state,
        summary: {
          ...state.summary,
          loading: true,
          loaded: false,
          searchCriteria: action.payload
        },
        summarySearch: {
          ...state.summarySearch,
          searchCriteria: action.payload
        }
      };

    case fromActions.ContractActionTypes.SortBy:
    case fromActions.ContractActionTypes.NextPage:
    case fromActions.ContractActionTypes.PrevPage:
    case fromActions.ContractActionTypes.FirstPage:
    case fromActions.ContractActionTypes.LastPage:
    case fromActions.ContractActionTypes.SetPageSize:
      return state;

    default: {
      if (Object.values(fromActions.ContractActionTypes).includes((action as fromActions.ContractActions).type)) {
        console.error('contract.reducer: unhandled action: ', action);
      }
    }
  }

  return state;
}

function updateSearchResultsState(state: SummaryState, response: fromModels.LoadSummaryResponse, headers: HttpHeaders) {
  let pagingSorting: fromModels.PagingSorting;

  // Grab special header we use for paging
  const pagingHeader: string = headers.get('X-Pagination');
  const pagingObject: object = JSON.parse(pagingHeader);

  // Populate paging information from http headers
  pagingSorting = {
    totalCount: pagingObject['totalCount'],
    pageSize: pagingObject['pageSize'],
    currentPage: pagingObject['currentPage'],
    totalPages: pagingObject['totalPages'],
    sortBy: pagingObject['sortBy'],
    sortDir: pagingObject['sortDir']
  };

  return {
    ...state,
    response,
    pagingSorting
  };
}

export const getContractSummary = (state: ContractDetailsState) => state.summary;
export const getContractSummarySearch = (state: ContractDetailsState) => state.summarySearch;
export const getContractRequestForm = (state: ContractDetailsState) => state.requestForm;
